import React, { createContext, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useRequestData } from 'ws-scripts/modules/common';

/**
 * Context providing the current page path for active-state computation.
 * Default value is '' (empty string) — if no provider is mounted,
 * useCurrentPath() returns '' and isNavItemActive(item, '') returns false.
 *
 * @type {React.Context<string>}
 */
const CurrentPathContext = createContext('');

/**
 * Provider that makes the current page path available to all descendants.
 * Wrap at the top of the NavigationDrawer tree so NavItem, NavLink, etc.
 * can consume via useCurrentPath() for SSR-safe active-state computation.
 *
 * Uses useState + useEffect to avoid a React 17 hydration mismatch:
 * - SSR renders with requestData.uri (the only source on the server).
 * - The initial client render uses the SAME value (from rehydrated Redux
 *   state) so the HTML is identical — no className diff for React to miss.
 * - useEffect fires after hydration and sets the real browser URL
 *   (window.location.pathname + search), triggering a re-render that
 *   applies active-state classes correctly.
 *
 * The drawer starts closed, so the brief delay is invisible to the user.
 */
const CurrentPathProvider = ({ children }) => {
	const requestData = useRequestData();
	const serverPath = (requestData && requestData.uri) || '';

	// Initialize with server path so SSR and initial client render match.
	const [currentPath, setCurrentPath] = useState(serverPath);

	// After hydration, switch to the real browser URL.
	useEffect(() => {
		setCurrentPath(window.location.pathname + window.location.search);
	}, []);

	return (
		<CurrentPathContext.Provider value={currentPath}>
			{children}
		</CurrentPathContext.Provider>
	);
};

CurrentPathProvider.propTypes = {
	children: PropTypes.node.isRequired
};

/**
 * Returns the current page path from CurrentPathContext.
 * Safe to call during SSR (returns requestData.uri) and on the client
 * (returns window.location.pathname).
 *
 * @returns {string}
 */
const useCurrentPath = () => useContext(CurrentPathContext);

export { CurrentPathContext, CurrentPathProvider, useCurrentPath };
